import classNames from 'classnames';
import { MotionValue } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';
import { useActiveSectionValue } from '@/shared/atoms/active-section';
import { Anchors, AppNavigation, Theme } from '@/shared/lib/types';
import { scrollToAnchor } from '@/shared/lib/utils/scrollToAnchor';
import ScrollTopButton from '../ScrollTopButton';

const ACTIVE_BAND_TOP_FRAC = 0.3;
const ACTIVE_BAND_BOTTOM_FRAC = 0.7;

function computeActiveAnchorId(anchors: Anchors): string | null {
    if (typeof window === 'undefined' || anchors.length === 0) return null;

    const h = window.innerHeight;
    const bandTop = h * ACTIVE_BAND_TOP_FRAC;
    const bandBottom = h * ACTIVE_BAND_BOTTOM_FRAC;
    const midY = h * 0.5;

    let bestOverlap = -1;
    let bestIndex = -1;

    anchors.forEach((anchor, index) => {
        const el = document.getElementById(anchor.id);
        if (!el) return;
        const r = el.getBoundingClientRect();
        const overlap = Math.max(0, Math.min(r.bottom, bandBottom) - Math.max(r.top, bandTop));

        if (
            overlap > bestOverlap ||
            (overlap === bestOverlap && overlap > 0 && (bestIndex === -1 || index < bestIndex))
        ) {
            bestOverlap = overlap;
            bestIndex = index;
        }
    });

    if (bestOverlap > 0 && bestIndex >= 0) {
        return anchors[bestIndex]?.id ?? null;
    }

    for (const anchor of anchors) {
        const el = document.getElementById(anchor.id);
        if (!el) continue;
        const r = el.getBoundingClientRect();
        if (r.top <= midY && r.bottom >= midY) return anchor.id;
    }

    let scrollSpyId: string | null = null;
    for (const anchor of anchors) {
        const el = document.getElementById(anchor.id);
        if (!el) continue;
        const r = el.getBoundingClientRect();
        if (r.top <= bandTop) scrollSpyId = anchor.id;
    }

    return scrollSpyId;
}

type Props = React.HTMLAttributes<HTMLElement> & {
    navigation: AppNavigation;
    anchors: Anchors;
    scrollYProgress?: MotionValue;
    theme?: Theme;
    universal?: boolean;
    embedded?: boolean;
};

function NavigationAnchors({ navigation, anchors, scrollYProgress, theme, universal, embedded, ...props }: Props) {
    const activeSection = useActiveSectionValue();
    const [activeAnchor, setActiveAnchor] = useState<string | null>(null);
    const anchorsRefs = useRef<Record<string, HTMLButtonElement | null>>({});
    const scrollingRef = useRef(false);
    const ANCHOR_SCROLLING_DURATION = 1000;
    const scrollEndTimeout = useRef<NodeJS.Timeout>();
    const [isTopButtonVisible, setIsTopButtonVisible] = useState(false);

    const navRef = useRef<HTMLDivElement | null>(null);
    const anchorsTheme = theme === 'light' ? 'light' : theme ? 'dark' : undefined;
    const hasAnchors = anchors?.length > 0;

    useEffect(() => {
        if (!hasAnchors) return;
        const updateHeight = () => {
            if (navRef.current) {
                document.documentElement.style.setProperty('--anchors-height', `${navRef.current.offsetHeight}px`);
            }
        };

        updateHeight();
        window.addEventListener('resize', updateHeight);

        return () => {
            window.removeEventListener('resize', updateHeight);

            if (embedded) {
                document.documentElement.style.setProperty('--anchors-height', '0px');
            }
        };
    }, [embedded, hasAnchors]);

    useEffect(() => {
        if (!hasAnchors) return;

        if (activeAnchor && anchorsRefs.current[activeAnchor]) {
            const activeElement = anchorsRefs.current[activeAnchor];
            const container = activeElement?.closest<HTMLElement>('.js-anchors-container');

            if (activeElement && container) {
                const containerRect = container.getBoundingClientRect();
                const elementRect = activeElement.getBoundingClientRect();
                const offsetInView = elementRect.left - containerRect.left;
                const targetOffsetFromLeft = container.clientWidth * 0.45;
                const delta = offsetInView - targetOffsetFromLeft;
                const maxScrollLeft = Math.max(0, container.scrollWidth - container.clientWidth);
                const nextScrollLeft = Math.min(Math.max(0, container.scrollLeft + delta), maxScrollLeft);

                if (Math.abs(nextScrollLeft - container.scrollLeft) > 1) {
                    container.scrollTo({ left: nextScrollLeft, behavior: 'smooth' });
                }
            }
        }
    }, [activeAnchor, hasAnchors]);

    useEffect(() => {
        if (!hasAnchors) return;

        let raf = 0;

        const run = () => {
            if (scrollingRef.current) return;
            setActiveAnchor(computeActiveAnchorId(anchors));
        };

        const schedule = () => {
            if (typeof window === 'undefined') return;
            cancelAnimationFrame(raf);
            raf = window.requestAnimationFrame(() => {
                raf = 0;
                run();
            });
        };

        schedule();
        window.addEventListener('scroll', schedule, { passive: true });
        window.addEventListener('resize', schedule);

        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener('scroll', schedule);
            window.removeEventListener('resize', schedule);
        };
    }, [anchors, activeSection, hasAnchors]);

    const onScrollEnd = () => {
        scrollingRef.current = false;
    };

    useEffect(() => {
        return () => {
            clearTimeout(scrollEndTimeout.current);
            document.removeEventListener('scrollend', onScrollEnd);
        };
    }, []);

    if (!hasAnchors) return null;

    return (
        <div
            {...props}
            className={classNames(
                'anchors-wrapper',
                {
                    'anchors-wrapper--embedded': embedded,
                    [`anchors-theme--${anchorsTheme}`]: anchorsTheme,
                },
                props.className,
            )}
            ref={navRef}
        >
            <div
                className={classNames('anchors wrapper text-xs', {
                    'anchors-visible-top-btn': isTopButtonVisible,
                })}
            >
                <div className="anchors-container-outer">
                    <div className="anchors-container js-anchors-container">
                        {anchors.map((anchor) => (
                            <button
                                key={anchor.id}
                                ref={(el) => {
                                    anchorsRefs.current[anchor.id] = el;
                                }}
                                className={classNames('anchor-item', {
                                    'is-active': activeAnchor === anchor.id,
                                })}
                                onClick={() => {
                                    setActiveAnchor(anchor.id);
                                    scrollToAnchor(anchor.id);
                                    scrollingRef.current = true;
                                    document.addEventListener('scrollend', onScrollEnd, { once: true });
                                    clearTimeout(scrollEndTimeout.current);
                                    scrollEndTimeout.current = setTimeout(onScrollEnd, ANCHOR_SCROLLING_DURATION); // Safari не поддерживает событие 'scrollend'
                                }}
                            >
                                {anchor.name}
                            </button>
                        ))}
                    </div>
                </div>

                {scrollYProgress && theme && (
                    <ScrollTopButton
                        theme={theme}
                        scrollYProgress={scrollYProgress}
                        {...(universal ? { universal } : {})}
                        onVisibilityChange={setIsTopButtonVisible}
                    />
                )}
            </div>
        </div>
    );
}

export default NavigationAnchors;
